import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { UserX, Send, X, Loader2, Ban, Search } from "lucide-react";
import { toast } from "sonner";

function AbsenceCard({ a, onSent, onDismissed }) {
  const [body, setBody] = useState(a.message_body || "");
  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const phones = a.phones || [];
  const active = phones.filter((p) => !p.opted_out);

  const doSend = async () => {
    if (!body.trim()) return toast.error("Message can't be empty");
    if (active.length === 0) return toast.error("No active phones on file for this student");
    setSending(true);
    try {
      const { data } = await api.post(`/absences/${a.id}/send`, { body, shorten_links: true });
      toast.success(`Sent ${data.sent_count} text${data.sent_count === 1 ? "" : "s"} · reply lands in the Inbox`);
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  const doDismiss = async () => {
    setDismissing(true);
    try {
      await api.post(`/absences/${a.id}/dismiss`);
      toast.success("Dismissed");
      onDismissed && onDismissed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDismissing(false); }
  };

  return (
    <div className="border-b border-slate-100 last:border-0 p-4 hover:bg-slate-50/40 transition-colors" data-testid={`absence-card-${a.id}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-slate-900">{a.student_name}</span>
            <span className="text-slate-400 text-sm">·</span>
            <span className="text-sm text-slate-700">{a.class_name}</span>
            {a.division && <Badge variant="secondary" className="text-[10px] uppercase">{a.division}</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>Absent {a.absent_date}</span>
            <span>·</span>
            <span>Texting <span className="font-semibold text-slate-700">{active.length}</span> phone{active.length === 1 ? "" : "s"} on file</span>
            {phones.filter((p) => p.opted_out).length > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                <Ban className="w-3 h-3" /> {phones.filter((p) => p.opted_out).length} opted-out will be skipped
              </span>
            )}
          </div>
          {phones.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {phones.map((p) => (
                <Badge key={p.contact_id} variant="secondary" className={`text-[10px] font-mono ${p.opted_out ? "line-through opacity-60" : ""}`}>{p.phone}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="min-h-[80px] resize-none border-slate-200 mt-3"
        data-testid={`absence-body-${a.id}`}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-slate-400">{body.length} chars · ~{Math.max(1, Math.ceil(body.length / 160))} segment{Math.ceil(body.length / 160) === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={doDismiss}
            disabled={dismissing || sending}
            className="text-slate-500 hover:text-[#D9232E]"
            data-testid={`absence-dismiss-${a.id}`}
          >
            {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" /> Dismiss</>}
          </Button>
          <Button
            onClick={doSend}
            disabled={sending || dismissing || !body.trim() || active.length === 0}
            size="sm"
            className="bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold"
            data-testid={`absence-send-${a.id}`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send to {active.length}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Absences() {
  const [data, setData] = useState({ recreational: [], competitive: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [dismissingAll, setDismissingAll] = useState("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/absences");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const dismissAll = async (program) => {
    const label = program === "competitive" ? "competitive" : "recreational";
    const count = program === "competitive" ? data.competitive.length : data.recreational.length;
    if (count === 0) return;
    if (!window.confirm(`Dismiss all ${count} ${label} absence${count === 1 ? "" : "s"}? This can't be undone.`)) return;
    setDismissingAll(program);
    try {
      const { data: res } = await api.post(`/absences/dismiss-all?program=${encodeURIComponent(program)}`);
      toast.success(`Dismissed ${res.dismissed_count} absence${res.dismissed_count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDismissingAll("");
    }
  };

  const empty = (label) => (search.trim() || range !== "all") ? (
    <div className="p-10 text-center text-sm text-slate-400" data-testid="absences-no-matches">No {label} absences match your filters.</div>
  ) : (
    <div className="p-10 text-center text-slate-400">
      <UserX className="w-8 h-8 mx-auto mb-2 text-slate-300" />
      <div className="text-sm">No {label} absences to text right now.</div>
      <div className="text-xs mt-1">New absences from Zapier will land here for review.</div>
    </div>
  );

  const ctDay = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
  const todayStr = ctDay(new Date());
  const weekStart = (() => { const t = new Date(`${todayStr}T12:00:00`); t.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return t.toLocaleDateString("en-CA"); })();
  const absDay = (a) => a.absent_date || (a.created_at ? ctDay(new Date(a.created_at)) : "");
  const inRange = (a) => range === "all" || (range === "today" ? absDay(a) === todayStr : absDay(a) >= weekStart);
  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const matches = (a) => !q ||
    (a.student_name || "").toLowerCase().includes(q) ||
    (a.class_name || "").toLowerCase().includes(q) ||
    (qDigits.length >= 3 && (a.phones || []).some((p) => (p.phone || "").includes(qDigits)));
  const rec = data.recreational.filter((a) => matches(a) && inRange(a));
  const comp = data.competitive.filter((a) => matches(a) && inRange(a));

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="absences-page">
      <div className="max-w-4xl mx-auto p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <UserX className="w-7 h-7 text-[#D9232E]" /> Absences
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every pending absence stays here until you <span className="font-semibold text-slate-700">Send</span> the text or <span className="font-semibold text-slate-700">Dismiss</span> it. Replies land in your normal Inbox.
          </p>
        </div>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by student, class or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 bg-white border-slate-200"
            data-testid="absences-search-input"
          />
        </div>
        <div className="flex gap-1.5 mb-4" data-testid="absences-range-filter">
          {[["all", "All"], ["today", "Today"], ["week", "This week"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              data-testid={`absences-range-${k}`}
              className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${range === k ? "bg-[#D9232E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <Tabs defaultValue="rec" className="w-full">
          <TabsList className="bg-slate-100 mb-4" data-testid="absences-tabs">
            <TabsTrigger value="rec" data-testid="absences-tab-rec">
              Recreational <span className="ml-2 text-[11px] rounded-full bg-white text-slate-700 px-1.5 py-0.5 font-semibold">{rec.length}</span>
            </TabsTrigger>
            <TabsTrigger value="comp" data-testid="absences-tab-comp">
              Competitive <span className="ml-2 text-[11px] rounded-full bg-white text-slate-700 px-1.5 py-0.5 font-semibold">{comp.length}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rec" className="mt-0">
            {data.recreational.length > 0 && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dismissAll("recreational")}
                  disabled={dismissingAll === "recreational"}
                  className="text-slate-600 hover:text-[#D9232E] hover:border-[#D9232E] border-slate-300"
                  data-testid="absences-dismiss-all-rec"
                >
                  {dismissingAll === "recreational" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <X className="w-4 h-4 mr-1.5" />}
                  Dismiss all {data.recreational.length}
                </Button>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {loading ? <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
                : rec.length === 0 ? empty("recreational")
                : rec.map((a) => <AbsenceCard key={a.id} a={a} onSent={load} onDismissed={load} />)}
            </div>
          </TabsContent>

          <TabsContent value="comp" className="mt-0">
            {data.competitive.length > 0 && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dismissAll("competitive")}
                  disabled={dismissingAll === "competitive"}
                  className="text-slate-600 hover:text-[#D9232E] hover:border-[#D9232E] border-slate-300"
                  data-testid="absences-dismiss-all-comp"
                >
                  {dismissingAll === "competitive" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <X className="w-4 h-4 mr-1.5" />}
                  Dismiss all {data.competitive.length}
                </Button>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {loading ? <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
                : comp.length === 0 ? empty("competitive")
                : comp.map((a) => <AbsenceCard key={a.id} a={a} onSent={load} onDismissed={load} />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
